import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../../../hooks/use-auth";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Card,
  Container,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  CircularProgress
} from "@mui/material";
import CustumerDetailsDraggableDrawer from ".././../../components/dashboard/customer/custumer_detail_draggable_drawer.js";
import colors from './../../../theme/colors.js';
import LinkIcon from "@mui/icons-material/Link";
import { Scrollbar } from "../../../components/scrollbar";
import { useMounted } from "../../../hooks/use-mounted";
import { userApi } from "../../../api/user-api";
import SearchIcon from "@mui/icons-material/Search";

const Customers = (props) => {
  const isMounted = useMounted();
  const {
    customersCount,
    onPageChange,
    onRowsPerPageChange,
    page,
    rowsPerPage,
    ...other
  } = props;

  const [customers, setCustomers] = useState([]);
  const [searchName, setSearchName] = useState("");
  const [match, setMatch] = useState(false);
  const [pagin, setPagin] = useState(0);
  const [totalNumberOfUsers, setTotalNumberOfUsers] = useState(0);
  const [limit, setLimit] = useState(25);
  const [permissions, setPermissions] = useState([]);
  const [isLoadingCustomers, setIsLoadingCustomers] = useState(true);
  
  const searchDebounceRef = useRef(null);
  const isMobile = useMediaQuery('(max-width:800px)');

  const { user } = useAuth();
  const tenant = useMemo(() => globalThis.localStorage.getItem('tenant'), []);

  const getPermissionsByRole = async (roles_id) => {
    const cacheKey = `permissions_${tenant}_${roles_id}`;
    const cached = JSON.parse(globalThis.localStorage.getItem(cacheKey) || 'null');
    if (cached && Date.now() - cached.timestamp < 5 * 60 * 1000) {
      setPermissions(cached.permissions);
      return;
    }
    const url = `${process.env.REACT_APP_BACK}/subdomain/${tenant}/api/permission-verification/get/`;
    const accessToken = globalThis.localStorage.getItem(`accessToken_${tenant}`);
    try {
      const resp = await fetch(url, {
        method: "POST",
        body: JSON.stringify({ roles_id }),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      });
      if (!resp.ok) throw new Error(`HTTP error! Status: ${resp.status}`);
      const json = await resp.json();
      globalThis.localStorage.setItem(cacheKey, JSON.stringify({ permissions: json.permissions, timestamp: Date.now() }));
      setPermissions(json.permissions);
    } catch (error) {
      console.error("Error fetching permissions:", error);
    }
  };

  const hasViewApprenantsPermission = useMemo(
    () => permissions.some(p => p.component === "Apprenants" && p.permission === "Voir"),
    [permissions]
  );

  useEffect(() => {
    if (!user?.roles_id) return;
    // Charger permissions et apprenants en parallèle
    getPermissionsByRole(user.roles_id);
    getCustomers(0, limit);
  }, [user]);

  const getCustomers = useCallback(async (pg, lmt) => {
    try {
      setIsLoadingCustomers(true);
      const data = await userApi.getUsers(user?.admin_id, pg, lmt);
      if (isMounted()) {
        setCustomers(data.users || []);
        setTotalNumberOfUsers(data.users_number || 0);
        setMatch(true);
      }
    } catch (err) {
      console.error(err);
      if (isMounted()) {
        setCustomers([]);
        setMatch(false);
      }
    } finally {
      if (isMounted()) setIsLoadingCustomers(false);
    }
  }, [isMounted, user]);

  const handleCustomers = useCallback(async (searchQuery, pg, lmt) => {
    try {
      setIsLoadingCustomers(true);
      const data = await userApi.searchUsers(user?.admin_id, searchQuery, pg, lmt);
      if (isMounted()) {
        const results = data.search_results || data.users || [];
        setCustomers(results);
        setTotalNumberOfUsers(data.users_number || 0);
        setMatch(true);
      }
    } catch (err) {
      console.error(err);
      if (isMounted()) {
        setCustomers([]);
        setTotalNumberOfUsers(0);
        setMatch(false);
      }
    } finally {
      if (isMounted()) setIsLoadingCustomers(false);
    }
  }, [isMounted, user]);

  useEffect(() => {
    clearTimeout(searchDebounceRef.current);

    searchDebounceRef.current = setTimeout(() => {
      if (searchName.length >= 3) {
        setPagin(0);
        handleCustomers(searchName, 0, limit);
      } else if (searchName.length === 0) {
        setPagin(0);
        getCustomers(0, limit);
      }
    }, 400);

    return () => clearTimeout(searchDebounceRef.current);
  }, [searchName]);

  const handleLimitChange = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    setLimit(newLimit);
    setPagin(0);
    
    if (searchName.length >= 3) {
      handleCustomers(searchName, 0, newLimit);
    } else {
      getCustomers(0, newLimit);
    }
  };

  const handlePageChange = (event, newPage) => {
    setPagin(newPage);
    if (searchName.length >= 3) {
      handleCustomers(searchName, newPage, limit);
    } else {
      getCustomers(newPage, limit);
    }
  };

  return (
    <>
      <Box component="main" sx={{ flexGrow: 1, py: 11 }}>
        <Container maxWidth="x1">
          <Box sx={{ mb: 5 }}>
            <Grid container justifyContent="space-between" spacing={3}>
              <Grid item>
                <Typography variant="h4" sx={{fontFamily: "Montserrat"}}>Apprenants</Typography>
              </Grid>
            </Grid>
          </Box>
          {hasViewApprenantsPermission ? (
            <Card>
              <Divider />
              <Box sx={{ alignItems: "center", display: "flex", flexWrap: "wrap", m: -1, p: 3 }}>
                <Box sx={{ m: 1, maxWidth: "100%", width: 450 }}>
                  <TextField
                    value={searchName}
                    onChange={(e) => setSearchName(e.target.value)}
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                    placeholder="Chercher un apprenant par nom, prénom ou email"
                  />
                </Box>
              </Box>
              <Scrollbar>
                {isLoadingCustomers ? (
                  <Box sx={{ p: 2 }}>
                    {[...Array(8)].map((_, i) => (
                      <Skeleton key={i} variant="rectangular" height={52} sx={{ mb: 1, borderRadius: 1 }} />
                    ))}
                  </Box>
                ) : (
                <Table sx={{ minWidth: 700 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ p: 2 }}>
                        <Typography sx={{ mx: 3 }} fontWeight="Bold" fontSize={13}>Nom</Typography>
                      </TableCell>
                      <TableCell sx={{ p: 2 }}>
                        <Typography sx={{ mx: 3 }} fontWeight="Bold" fontSize={13}>Email</Typography>
                      </TableCell>
                      <TableCell sx={{ p: 2 }}>
                        <Typography sx={{ mx: 3 }} fontWeight="Bold" fontSize={13}>Tlms</Typography>
                      </TableCell>
                      <TableCell sx={{ p: 2 }}>
                        <Typography sx={{ mx: 3 }} fontWeight="Bold" fontSize={13}>Syna</Typography>
                      </TableCell>
                      {!isMobile && (
                        <TableCell sx={{ p: 2, display: "flex", justifyContent: "center" }}>
                          <Typography sx={{ mx: 3 }} fontWeight="Bold" fontSize={13}>Vue rapide</Typography>
                        </TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {customers.length === 0 && !isLoadingCustomers ? (
                      <TableRow>
                        <TableCell colSpan={isMobile ? 4 : 5} align="center">
                          <Typography variant="subtitle1" sx={{ py: 3 }}>
                            Aucun apprenant trouvé.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      customers.map((customer) => (
                        <TableRow hover key={customer.id}>
                          <TableCell>
                            <Box sx={{ alignItems: "center", display: "flex", ml: 2 }}>
                              <RouterLink
                                to={`/${tenant}/dashboard/customers/${customer.id}`}
                                style={{ textDecoration: "none" }}
                              >
                                <Typography
                                  color={(theme) => theme.palette.mode === "dark" ? colors.secondary : colors.primary}
                                  fontWeight="Bold"
                                  fontSize={14}
                                >
                                  {customer.firstname} {customer.lastname}
                                </Typography>
                              </RouterLink>
                            </Box>
                          </TableCell>

                          <TableCell>
                            <Box sx={{ alignItems: "center", display: "flex", ml: 1 }}>
                              <Typography
                                color={(theme) => theme.palette.mode === "dark" ? "#F5F6FC" : colors.primary}
                                fontSize={15}
                              >
                                {customer.email}
                              </Typography>
                            </Box>
                          </TableCell>
                          
                          <TableCell>
                            <Box sx={{ alignItems: "center", display: "flex", ml: 3 }}>
                              {customer.tlms_id && (
                                <IconButton
                                  href={`https://${customer.tlms_domain}/user/info/id:${customer.tlms_id}`}
                                  target="_blank"
                                >
                                  <LinkIcon fontSize="small" />
                                </IconButton>
                              )}
                            </Box>
                          </TableCell>
                          
                          <TableCell>
                            <Box sx={{ alignItems: "center", display: "flex", ml: 3 }}>
                              <IconButton
                                href={`https://${tenant}.syndays.fr/dashboard/customers/${customer.syna_id}`}
                                target="_blank"
                              >
                                <LinkIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </TableCell>
                          
                          {!isMobile && (
                            <TableCell>
                              <Box sx={{ alignItems: "center", display: "flex", alignContent: "center", height:"100%", width:"100%" }}>
                                <CustumerDetailsDraggableDrawer 
                                  customer_id={customer.id} 
                                  customer_name={`${customer.firstname} ${customer.lastname}`} 
                                />
                              </Box>
                            </TableCell>
                          )}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
                )}
              </Scrollbar>
              <TablePagination
                component="div"
                count={totalNumberOfUsers}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleLimitChange}
                page={pagin}
                labelRowsPerPage="Lignes par page :"
                rowsPerPage={limit}
                rowsPerPageOptions={[25, 50, 100]}
              />
            </Card>
          ) : (!match ? (
            <Box display="flex" justifyContent="center" alignItems="center" padding={5}>
              <Typography color="inherit" size={150} />
            </Box>
          ) : (
            <Box display="flex" justifyContent="center" alignItems="center" padding={5}>
              <CircularProgress color="inherit" size={150} />
            </Box>
          ))}
        </Container>
      </Box>
    </>
  );
};

export default Customers;